import { useState } from 'react'
import { Link } from 'react-router-dom'
import { tenantConfig } from '../../config/tenant'
import Nav from '../../components/Nav'
import Seo from '../../components/Seo'
import { faqSchema } from '../../config/seo'
import Footer from '../../components/Footer'
import LeadCaptureModal from '../../components/LeadCaptureModal'

/**
 * ServicesPage — ported from docs/SaaS/greystone-mockups-light/services.html
 * with the §4.1 simplification cuts applied (locked D15):
 *   KEEP → hero + 4 core categories (Home loans / Refinance / Investment /
 *          Commercial) + FAQ + CTA.
 *   DROP → Construction and Car & Personal categories. They fold into the
 *          single "Something else? Enquire" CTA below the four cores.
 * SaaS integration → every CTA opens the LeadCaptureModal (POST /api/leads).
 */

interface Category {
  id: string
  label: string
  tagline: string
  body: string
  points: string[]
  /** Decorative Heroicon path (aria-hidden) — retained as a fallback. */
  icon: string
  gradient: string
  /** Category hero photo (public/assets/services). */
  image: string
}

const CATEGORIES: Category[] = [
  {
    id: 'home-loans',
    image: '/assets/services/home-loans.jpeg',
    label: 'Home loans',
    tagline: 'Your first home, or your next one.',
    body:
      "Buying a home is one of life's biggest decisions. We make it simpler, explaining your options, helping with grants and LMI, and matching you with the lender that fits your situation.",
    points: [
      'First Home Owner Grant & First Home Guarantee guidance',
      'Access to 60+ lenders with the best rates and features',
      'LMI strategies to minimise your deposit requirements',
      'Pre-approval support to make your offer competitive',
    ],
    icon:
      'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    gradient: 'from-[#F4FBD9] via-[#FAFAF7] to-[#F4F4F0]',
  },
  {
    id: 'refinance',
    image: '/assets/services/refinance.jpeg',
    label: 'Refinance',
    tagline: 'Save thousands. Switch smart.',
    body:
      "Many homeowners are paying more than they need to. We'll review your current loan and find better deals across our panel of 60+ lenders, often saving you hundreds per month.",
    points: [
      'Free loan health check & savings analysis',
      'Cashback & switching incentive opportunities',
      'Debt consolidation to simplify your finances',
      'Equity release for renovations or investment',
    ],
    icon:
      'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    gradient: 'from-[#F4F4F0] to-[#F4FBD9]',
  },
  {
    id: 'investment',
    image: '/assets/services/investment.jpeg',
    label: 'Investment',
    tagline: 'Build your portfolio, smarter.',
    body:
      "Whether you're acquiring your first investment property or expanding a portfolio, we help structure your lending to maximise borrowing capacity and tax efficiency.",
    points: [
      'Interest-only & investor-friendly loan products',
      'Portfolio strategy & borrowing capacity planning',
      'Cross-collateralisation analysis & restructuring',
      'SMSF lending for self-managed super fund investments',
    ],
    icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    gradient: 'from-[#F4FBD9] via-[#FAFAF7] to-[#F4F4F0]',
  },
  {
    id: 'commercial',
    image: '/assets/services/commercial.jpeg',
    label: 'Commercial',
    tagline: 'Finance for business growth.',
    body:
      'From commercial property and asset finance to working capital and development funding, we specialise in complex business lending that traditional banks find too hard.',
    points: [
      'Commercial property purchasing and refinancing',
      'Business purchase funding and cashflow lending',
      'Property Investment purchasing and refinancing',
      'Asset and Equipment Finance for vehicles and machinery',
    ],
    icon:
      'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    gradient: 'from-[#F4F4F0] via-[#F4FBD9] to-[#FAFAF7]',
  },
  {
    id: 'construction',
    image: '/assets/services/construction.jpeg',
    label: 'Construction',
    tagline: 'Build it right, funded right.',
    body:
      'Building a new home or developing a site runs to its own rhythm, progressive drawdowns, builder contracts and council approvals. We structure construction lending so the funds are there at each stage, without the stress.',
    points: [
      'Owner-occupier & investment construction loans',
      'Progressive drawdown facilities aligned to building stages',
      'Knock-down rebuild & major renovation finance',
      'Small development & subdivision funding',
    ],
    icon:
      'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z',
    gradient: 'from-[#F4FBD9] via-[#FAFAF7] to-[#F4F4F0]',
  },
]

const FAQS = [
  {
    q: 'Does using a broker cost me anything?',
    a: "In almost all cases, no. We're paid a commission by the lender once your loan settles, so our service is free to you. If a fee ever did apply, we'd tell you upfront and in writing before you commit to anything.",
  },
  {
    q: 'How many lenders do you compare?',
    a: 'We work across a panel of 60+ lenders, the major banks, second-tier lenders and specialist providers, to match you with the rate and loan structure that fits your situation.',
  },
  {
    q: 'How long does approval take?',
    a: "It depends on the lender and how complete your documents are, but conditional approval is often a few business days. We'll give you a realistic timeline once we've seen your scenario.",
  },
  {
    q: "Can you help if I've been knocked back before?",
    a: "Often, yes. A decline with one lender doesn't mean the door is closed everywhere, different lenders assess income, credit and serviceability differently. We'll find the ones most likely to say yes.",
  },
  {
    q: 'Do you handle car or personal loans too?',
    a: 'Yes. Alongside our core home, refinance, investment, commercial and construction lending, we can also help with car and personal finance, just hit the Enquire button and tell us what you need.',
  },
  {
    q: 'How much deposit do I need, and what is LMI?',
    a: "Lenders typically look for around a 20% deposit, but there are ways in with less. With a smaller deposit you'll usually pay Lenders Mortgage Insurance (LMI), a one-off cost that protects the lender, not you, and can often be added to the loan. There may also be grants or schemes that help, so tell us your situation and we'll map out your options.",
  },
  {
    q: 'Should I use a bank or a broker?',
    a: "A bank can only offer you its own products. A broker compares across 60+ lenders to find the right fit, and under Best Interest Duty, brokers are legally required to act in your best interests, a duty banks don't have. You get more choice, and someone on your side.",
  },
  {
    q: 'What is pre-approval and do I need it?',
    a: "Pre-approval (or conditional approval) is a lender's indication of what you could borrow before you've found a property, it lets you shop with confidence and move quickly. It isn't a full approval: the lender still verifies your details and assesses the specific property before it's formal. We'll arrange it as part of getting you ready to buy.",
  },
]

export default function ServicesPage() {
  const [leadOpen, setLeadOpen] = useState(false)

  return (
    <>
      <Seo
        title="Our Services"
        path="/services"
        description="Home loans, refinancing, investment and construction lending, see how our brokers help across every scenario."
        jsonLd={faqSchema(FAQS)}
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-5xl mx-auto px-6 pb-10 text-center">
          <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-3">Our services</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink font-semibold leading-tight mb-5">
            Lending for every chapter of life.
          </h1>
          <p className="text-lg text-gs-text-soft max-w-2xl mx-auto">
            Whether you&apos;re buying, refinancing, investing, or scaling a business, we&apos;ve got the
            lender access and expertise to make it happen.
          </p>
        </section>

        {/* QUICK NAV PILLS — jump to the four core categories */}
        <section className="pb-8">
          <div className="max-w-5xl mx-auto px-6 flex flex-wrap justify-center gap-2">
            {CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className="bg-white border border-[#E8E8E2] text-ink text-sm font-medium px-4 py-2 rounded-full hover:border-ink hover:shadow-sm transition-all"
              >
                {c.label}
              </a>
            ))}
          </div>
        </section>

        {/* CORE CATEGORIES (exactly 4) */}
        {CATEGORIES.map((cat, i) => {
          const reversed = i % 2 === 1
          return (
            <section
              key={cat.id}
              id={cat.id}
              className={`py-14 lg:py-20 ${i % 2 === 0 ? 'bg-white border-y border-[#E8E8E2]' : ''}`}
              style={{ scrollMarginTop: '100px' }}
            >
              <div className="max-w-6xl mx-auto px-6 grid gap-10 lg:gap-16 lg:grid-cols-2 items-center">
                <div className={reversed ? 'lg:order-2' : ''}>
                  <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-ink font-semibold">
                    {cat.label}
                  </h2>
                  <p className="text-lg text-gs-text-soft mt-3">{cat.tagline}</p>
                  <p className="text-gs-text-soft mt-4 leading-relaxed">{cat.body}</p>
                  <ul className="mt-6 space-y-3">
                    {cat.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-gs-text">
                        <span
                          className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: tenantConfig.accentColor }}
                          aria-hidden="true"
                        >
                          <svg className="w-3 h-3 text-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={() => setLeadOpen(true)}
                      className="px-6 py-3 rounded-lg font-semibold text-sm"
                      style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                    >
                      Talk to a broker
                    </button>
                    <Link
                      to="/tools"
                      className="px-6 py-3 rounded-lg font-semibold text-sm border border-[#D8D8D0] hover:bg-white transition-colors"
                    >
                      Compare rates
                    </Link>
                  </div>
                </div>
                <div
                  className={`${reversed ? 'lg:order-1' : ''} aspect-square rounded-3xl overflow-hidden shadow-sm bg-gradient-to-br ${cat.gradient}`}
                >
                  <img
                    src={cat.image}
                    alt={`${cat.label} · Greystone Finance`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>
          )
        })}

        {/* "SOMETHING ELSE?" — folds in dropped Construction / Car & Personal */}
        <section className="max-w-6xl mx-auto px-6 py-14">
          <div className="rounded-2xl border border-[#E8E8E2] bg-white p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-ink">Something else?</h2>
              <p className="text-gs-text-soft mt-1 max-w-xl">
                Building a home, buying a car, consolidating debt or a personal loan, we can help with
                that too. Tell us what you need and we&apos;ll point you the right way.
              </p>
            </div>
            <button
              onClick={() => setLeadOpen(true)}
              className="px-6 py-3.5 rounded-lg font-semibold text-sm whitespace-nowrap self-start md:self-auto"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Enquire
            </button>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="max-w-4xl mx-auto px-6 py-10" style={{ scrollMarginTop: '100px' }}>
          <div className="mb-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">Good to know</div>
            <h2 className="font-heading text-3xl md:text-4xl text-ink">Frequently asked questions</h2>
          </div>
          <div className="border-y border-[#E8E8E2] divide-y divide-[#E8E8E2]">
            {FAQS.map((f) => (
              <div key={f.q} className="py-5">
                <h3 className="font-heading text-lg text-ink">{f.q}</h3>
                <p className="text-gs-text-soft mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA BAND */}
        <section className="max-w-6xl mx-auto px-6 pt-8">
          <div
            className="rounded-3xl p-10 md:p-14 grid gap-6 md:grid-cols-[1.5fr_1fr] items-center"
            style={{ background: tenantConfig.primaryColor }}
          >
            <div>
              <h2 className="font-heading text-3xl md:text-4xl mb-3" style={{ color: tenantConfig.accentColor }}>
                Not sure which loan is right?
              </h2>
              <p className="text-white/80 text-lg">
                Tell us your situation and we&apos;ll compare the market for you, no fees for you, no
                obligation, just a straight answer.
              </p>
            </div>
            <button
              onClick={() => setLeadOpen(true)}
              className="px-6 py-4 rounded-lg font-semibold text-base w-full md:w-auto justify-self-start md:justify-self-end"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Talk to a broker
            </button>
          </div>
        </section>
      </main>
      <Footer />

      <LeadCaptureModal open={leadOpen} onClose={() => setLeadOpen(false)} source="website" />
    </>
  )
}
